import { useState, useEffect } from 'react';

const STORAGE_KEY = 't24_theme_preference';

/**
 * Hook for managing theme (light/dark mode) with system preference detection
 *
 * Features:
 * - Detects system preference on first visit
 * - Persists user choice to localStorage
 * - Applies data-theme attribute to document root
 * - Provides toggle function for switching themes
 */
export function useTheme() {
  // Initialize theme from localStorage or system preference
  const [theme, setTheme] = useState(() => {
    try {
      // Check localStorage first
      const savedTheme = localStorage.getItem(STORAGE_KEY);
      if (savedTheme === 'light' || savedTheme === 'dark') {
        return savedTheme;
      }

      // Fall back to system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }

      // Default to dark theme (current design)
      return 'dark';
    } catch (err) {
      console.error('Failed to load theme preference:', err);
      return 'dark';
    }
  });

  // Apply theme to document root on mount and when theme changes
  useEffect(() => {
    try {
      // Set data-theme attribute on document root
      document.documentElement.setAttribute('data-theme', theme);

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (err) {
      console.error('Failed to apply theme:', err);
    }
  }, [theme]);

  // Listen for system theme changes (optional enhancement)
  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e) => {
        // Only auto-switch if user hasn't manually set a preference
        const savedTheme = localStorage.getItem(STORAGE_KEY);
        if (!savedTheme) {
          setTheme(e.matches ? 'dark' : 'light');
        }
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    } catch (err) {
      console.error('Failed to listen for system theme changes:', err);
    }
  }, []);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Set specific theme
  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  // Check if current theme is light/dark
  const isLight = theme === 'light';
  const isDark = theme === 'dark';

  return {
    theme,
    isLight,
    isDark,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
  };
}
